import React from 'react';
import { cn } from '../../lib/utils';

// CSS-based shapes (dot, circle, square, diamond, dash) — unchanged
const CSS_SIZES = {
  1: { w: 16, h: 16 },
  2: { w: 14, h: 14 },
  3: { w: 12, h: 12 },
  4: { w: 10, h: 10 },
  5: { w: 10, h: 10 },
};

// Character-based shapes (arrows, check) — large for visibility
const CHAR_SIZES = {
  1: { w: 39, h: 39, font: '28px' },
  2: { w: 34, h: 34, font: '24px' },
  3: { w: 28, h: 28, font: '20px' },
  4: { w: 25, h: 25, font: '17px' },
  5: { w: 25, h: 25, font: '17px' },
};

const CHAR_MAP = { 'arrow-right': '▶', 'arrow-up': '▲', 'arrow-down': '▼', 'arrow-left': '◀', 'check': '✓' };
const isCharBullet = (s) => s in CHAR_MAP;

export const BulletShape = ({ bulletStyle = 'dot', color = '#8b5cf6', level = 1 }) => {
  const base = "flex-shrink-0 flex items-center justify-center";

  if (isCharBullet(bulletStyle)) {
    const { w, h, font } = CHAR_SIZES[level] || CHAR_SIZES[3];
    const dotW = (CSS_SIZES[level] || CSS_SIZES[3]).w;
    const ml = (dotW / 2) - (w / 2); // negative margin to center on spine
    return (
      <div
        className={cn(base, bulletStyle === 'check' && "font-bold")}
        style={{ width: w, height: h, color, fontSize: font, lineHeight: 1, marginLeft: ml, marginRight: ml }}
      >
        {CHAR_MAP[bulletStyle]}
      </div>
    );
  }

  const { w, h } = CSS_SIZES[level] || CSS_SIZES[3];

  switch (bulletStyle) {
    case 'circle':
      return <div className={cn(base, "rounded-full")} style={{ width: w, height: h, border: `2px solid ${color}` }} />;
    case 'square':
      return <div className={base} style={{ width: w, height: h, backgroundColor: color, borderRadius: 2 }} />;
    case 'diamond':
      return <div className={base} style={{ width: w, height: h }}><div className="rotate-45" style={{ backgroundColor: color, width: w * 0.65, height: h * 0.65, borderRadius: 1 }} /></div>;
    case 'dash':
      return <div className={base} style={{ width: w, height: h }}><div style={{ backgroundColor: color, width: w * 0.75, height: 2, borderRadius: 1 }} /></div>;
    default:
      return <div className={cn(base, "rounded-full shadow-sm")} style={{ width: w, height: h, backgroundColor: color }} />;
  }
};

// Public view — scaled up proportionally
const CSS_SIZES_PUB = {
  1: { w: 21, h: 21 },
  2: { w: 18, h: 18 },
  3: { w: 16, h: 16 },
  4: { w: 14, h: 14 },
  5: { w: 14, h: 14 },
};

const CHAR_SIZES_PUB = {
  1: { w: 50, h: 50, font: '36px' },
  2: { w: 45, h: 45, font: '31px' },
  3: { w: 36, h: 36, font: '25px' },
  4: { w: 31, h: 31, font: '21px' },
  5: { w: 31, h: 31, font: '21px' },
};

export const BulletShapePublic = ({ bulletStyle = 'dot', color = '#8b5cf6', level = 1 }) => {
  const base = "flex-shrink-0 flex items-center justify-center";

  if (isCharBullet(bulletStyle)) {
    const { w, h, font } = CHAR_SIZES_PUB[level] || CHAR_SIZES_PUB[3];
    const dotW = (CSS_SIZES_PUB[level] || CSS_SIZES_PUB[3]).w;
    const ml = (dotW / 2) - (w / 2);
    return (
      <div
        className={cn(base, bulletStyle === 'check' && "font-bold")}
        style={{ width: w, height: h, color, fontSize: font, lineHeight: 1, marginLeft: ml, marginRight: ml }}
      >
        {CHAR_MAP[bulletStyle]}
      </div>
    );
  }

  const { w, h } = CSS_SIZES_PUB[level] || CSS_SIZES_PUB[3];

  switch (bulletStyle) {
    case 'circle':
      return <div className={cn(base, "rounded-full")} style={{ width: w, height: h, border: `2px solid ${color}` }} />;
    case 'square':
      return <div className={base} style={{ width: w, height: h, backgroundColor: color, borderRadius: 2 }} />;
    case 'diamond':
      return <div className={base} style={{ width: w, height: h }}><div className="rotate-45" style={{ backgroundColor: color, width: w * 0.65, height: h * 0.65, borderRadius: 1 }} /></div>;
    case 'dash':
      return <div className={base} style={{ width: w, height: h }}><div style={{ backgroundColor: color, width: w * 0.75, height: 2.5, borderRadius: 1 }} /></div>;
    default:
      return <div className={cn(base, "rounded-full shadow-sm")} style={{ width: w, height: h, backgroundColor: color }} />;
  }
};
